<template>
  <div class="auth-card-wrapper">
    <div class="auth-card">
      <div class="auth-card-container">
        <img
          class="logo auth-logo"
          alt="Continuum Design"
          src="/continuum-design-logo.svg"
        />
        <h3 class="text-title mb-2">Confirm your new account</h3>
        <p class="reset-password-description">
          An email has been sent to you with a registration code. Please <br/>
          confirm your account by entering the registration code below.
        </p>
      </div>

      <v-form ref="form" v-model="valid">
        <div class="auth-card-container form-fields" style="margin-bottom: 8px">

          <v-text-field
            label="Email"
            type="email"
            v-model="username"
            :rules="[rules.required, rules.email]"
            validate-on="blur"
            variant="outlined"
            density="default"
          ></v-text-field>

          <v-text-field
            label="Enter your registration code"
            id="code"
            v-model="code"
            :rules="[rules.required]"
            validate-on="blur"
            v-on:keyup.enter="submit"
            variant="outlined"
            density="default"
          ></v-text-field>

          <div class="resend-container mb-2">
            <span
              v-if="!resendDisabled"
              @click="resendConfirmationCode"
              class="resend-link"
            >
              Resend code
            </span>
            <span v-else class="resend-timer">
              Resend code in {{ countdown }}s
            </span>
          </div>

          <v-row class="my-0">
            <v-col class="pa-0">
              <div v-if="message" class="message bg-success">{{ message }}</div>
              <div v-if="error" class="message bg-error">{{ error }}</div>
            </v-col>
          </v-row>

          <div class="d-flex justify-center auth-card-container">
            <v-btn
              min-width="180px"
              rounded="lg"
              size="large"
              color="primary"
              class="d-flex"
              variant="flat"
              @click="submit"
            >
              Confirm
            </v-btn>
          </div>

          <v-divider></v-divider>

          <div class="auth-card-footer-part">
            <div class="auth-card-footer">
              <p class="text">
                Already have an account?
                <router-link
                  class="text text-decoration-none"
                  tabindex="5"
                  :to="{ path: '/login'}"
                >
                  <span class="light--text">Login</span>
                </router-link>
              </p>
            </div>
          </div>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import { authProxyApi } from '@/api/authProxyApi'

export default {
  data () {
    return {
      valid: true,
      email: '',
      code: '',
      error: null,
      message: null,
      username: '',
      resendDisabled: false,
      countdown: 60,
      timer: null,
      rules: {
        required: (value) => !!value || 'Required.',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        }
      },
      e1: true
    }
  },
  created () {
    if (this.$route.query.username !== '') {
      this.username = this.$route.query.username
    }
    // When redirected here from /login or /reset-password because the email
    // wasn't verified yet, auto-trigger a fresh code so the user doesn't have
    // to figure out they need to click "Resend code" themselves.
    if (this.$route.query.autoResend && this.username) {
      this.resendConfirmationCode()
    }
  },
  beforeUnmount () {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    async submit () {
      this.error = null
      this.message = null

      const { valid } = await this.$refs.form.validate()
      if (valid) {
        try {
          const email = (this.username || '').toLowerCase()
          const code = (this.code || '').trim()

          const res = await authProxyApi.confirmSignup({ email, code })

          if (res?.code === 'ALREADY_CONFIRMED') {
            this.redirectAlreadyConfirmed(email)
            return
          }

          if (res?.code === 'CODE_EXPIRED') {
            // Unlock resend immediately — no reason to wait when the code is already dead.
            this.cancelCountdown()
            this.error = res.message
            return
          }

          if (!res?.ok) {
            this.error = res?.message || 'Unable to verify code. Please check and try again.'
            return
          }

          // Came from /reset-password — send them back to finish that flow.
          if (this.$route.query.intent === 'reset') {
            this.$router.replace({
              path: '/reset-password',
              query: { username: email }
            })
            return
          }

          this.$router.replace({
            path: '/login',
            query: {
              thanks: true,
              username: email,
              projectId: this.$route.query.projectId || undefined,
              redirect: this.$route.query.redirect || undefined
            }
          })
        } catch (err) {
          console.error('Confirm signup failed:', err)
          this.error = 'Unable to verify code. Please check and try again.'
        }
      }
    },
    async resendConfirmationCode () {
      // Guard against re-entry while a request is in flight or the cooldown
      // is active. Without this, rapid clicks before the API responds would
      // each fire a fresh resend (the link stays visible until startCountdown
      // runs).
      if (this.resendDisabled) return

      this.error = null
      this.message = null

      if (!this.username || this.rules.email(this.username) !== true) {
        this.error = 'Please provide a valid email address first.'
        return
      }

      // Start the cooldown synchronously, BEFORE awaiting the network call,
      // so subsequent clicks during the in-flight request are blocked.
      this.startCountdown()

      try {
        const email = (this.username || '').toLowerCase()
        const res = await authProxyApi.resendConfirmation({ email })

        if (res?.code === 'ALREADY_CONFIRMED') {
          this.redirectAlreadyConfirmed(email)
          return
        }

        // Generic message for the pending/unknown cases (still enumeration-safe).
        this.message = res?.message || 'If the action can be completed, you will receive an email shortly.'
      } catch (err) {
        console.error('Resend confirmation failed:', err)
        // Still show the same message to avoid leaking account state.
        this.message = 'If the action can be completed, you will receive an email shortly.'
      }
    },
    redirectAlreadyConfirmed (email) {
      this.$router.replace({
        path: '/login',
        query: {
          username: email,
          alreadyConfirmed: true,
          projectId: this.$route.query.projectId || undefined,
          redirect: this.$route.query.redirect || undefined
        }
      })
    },

    startCountdown () {
      // Defensive: clear any prior interval so two timers can never race,
      // each decrementing countdown once per second.
      if (this.timer) {
        clearInterval(this.timer)
      }
      this.resendDisabled = true
      this.countdown = 60

      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
        } else {
          clearInterval(this.timer)
          this.timer = null
          this.resendDisabled = false
        }
      }, 1000)
    },
    cancelCountdown () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      this.resendDisabled = false
      this.countdown = 0
    }
  }
}
</script>

<style scoped>
.text {
  font-size: 13px;
  margin-bottom: 0;
  line-height: 14.3px;
}

.logo {
  width: 180px;
  margin-bottom: 24px;
}

.form-fields {
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #fff;
}

.resend-container {
  font-size: 14px;
}

.resend-link {
  color: #1976D2;
  cursor: pointer;
}

.resend-link:hover {
  text-decoration: underline;
}

.resend-timer {
  color: #757575;
}

[data-theme="dark"] a,
[data-theme="dark"] a .light--text,
[data-theme="dark"] .resend-link {
  color: var(--blue-1);
}
</style>
